import { QueryClient } from "@tanstack/react-query";
import { getErrorStatus, isRetryableError, maxRetriesFor, retryDelayFor } from "./retryPolicy";

/**
 * App-wide react-query client.
 *
 * react-admin otherwise falls back to a bare `new QueryClient()`
 * (`ra-core/dist/core/CoreAdminContext.js`), i.e. react-query's default retry of
 * 3 (4 attempts) with no status discrimination. Retrying a 4xx is pointless —
 * the input won't change — and it actively harms UX: react-admin calls
 * `authProvider.checkError` on every failed attempt, so a retried 401/403 fires
 * the error notification once per attempt (the "same toast 5 times" bug).
 *
 * The 4xx short-circuit itself shipped in #4265; #3804 adds the 429/408
 * carve-outs, the delay floor for rate limiting, and — the substantive part —
 * shares this classification with the two hand-rolled poll loops that bypass
 * react-query entirely. Classification lives in `./retryPolicy` for that reason.
 *
 * NOTE on counts: `retry()` is evaluated BEFORE `failureCount` is incremented
 * (`@tanstack/query-core` retryer), so `failureCount < 3` permits 3 retries /
 * 4 total attempts. Mutations have no entry here, so writes keep react-query's
 * default of 0 retries.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // No `retryUnauthorized` here: a 401 in react-query recovers out of band
        // via checkError → onSilentRefresh, so retrying it would just re-toast.
        if (!isRetryableError(error)) return false;
        return failureCount < maxRetriesFor(getErrorStatus(error));
      },
      retryDelay: (failureCount, error) => retryDelayFor(failureCount, getErrorStatus(error)),
    },
  },
});
